import { peekVariant, resolveVariant, trackExposure, TEST } from './experimentAssignment';

/**
 * The /interview funnel comes in two shapes.
 *
 * Control is the funnel as it stands: it opens with the Decision question and
 * shows the full nav menu. The extended flow drops that opening question,
 * hides the menu, and gates entry with its own qualifying question — "Do you
 * already know which GLP-1 is right for you?" — before running the same
 * six-question route (bestyou through glp1-history) the variant has always
 * used. That gate lives outside EXTENDED_FLOW.steps on purpose: it is not one
 * of the six questions, and folding it in would renumber every step after it
 * and reshape the progress bar for a route that is already mid-experiment.
 *
 * A visitor is only ever enrolled by startInterview(), which a landing page CTA
 * calls on its way into the funnel. Every other caller reads the assignment
 * without creating one, so somebody who opens /interview directly is never
 * counted in the experiment and always sees control.
 */

export const FLAG_KEY = 'interview-v2-1';

// Share of landing-page submitters entering the experiment at all. Keep this in
// sync with the flag's rollout in PostHog, since a visitor whose flags are
// already cached is assigned by the flag rather than by the local coin flip.
const ENROLLED_SHARE = 1.0;

// Share of *enrolled* visitors who see the extended funnel.
const TEST_SHARE = 0.50;

export const CONTROL_FLOW = {
    id: 'control',
    hideNavigation: false,
    entryPath: '/interview',
    steps: [
        '/interview',
        '/interview/bestyou',
        '/interview/height-weight',
        '/interview/priority'
    ]
};

export const EXTENDED_FLOW = {
    id: 'extended',
    hideNavigation: true,
    // Where a landing CTA sends a newly-enrolled visitor. Deliberately not
    // steps[0]: the gate decides whether the visitor enters the six-question
    // route at all, the same way Decision decides entry into control, so it
    // is not one of the six questions and would renumber all of them if it
    // were folded into steps.
    entryPath: '/interview/glp1-known',
    steps: [
        '/interview/bestyou',
        '/interview/height-weight',
        '/interview/priority',
        '/interview/diet',
        '/interview/movement',
        '/interview/glp1-history'
    ]
};

/**
 * Enroll this visitor and report the exposure. This is the only place an
 * assignment is created. Call it from a landing CTA immediately before the
 * submit event is tracked, so the variant is registered on the person first and
 * that event carries it.
 *
 * @returns {{ id: string, hideNavigation: boolean, entryPath: string, steps: string[] }}
 *          the flow this visitor should follow; navigate to its entryPath
 */
export function startInterview() {
    const { variant } = resolveVariant({
        flagKey: FLAG_KEY,
        enrolledShare: ENROLLED_SHARE,
        testShare: TEST_SHARE
    });

    trackExposure(FLAG_KEY, variant);

    return variant === TEST ? EXTENDED_FLOW : CONTROL_FLOW;
}

/**
 * Read this visitor's flow without enrolling them. Anyone who did not arrive
 * through a landing CTA has no assignment and gets the control funnel.
 */
export function getInterviewFlow() {
    return peekVariant(FLAG_KEY).variant === TEST ? EXTENDED_FLOW : CONTROL_FLOW;
}

// The three helpers below are total: a path that is not part of the given flow
// returns a safe value rather than undefined. navigate(undefined) is read by
// react-router as navigate(0), which reloads the page — a loop with no exit.

export function stepPosition(flow, path) {
    const index = flow.steps.indexOf(path);
    return index === -1 ? 1 : index + 1;
}

export function nextStep(flow, path) {
    const index = flow.steps.indexOf(path);
    return index === -1 ? null : flow.steps[index + 1] ?? null;
}

export function previousStep(flow, path) {
    const index = flow.steps.indexOf(path);
    return index <= 0 ? null : flow.steps[index - 1];
}
